import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress, Divider,
    Switch, Input,
    IconButton,
    Modal,
    ModalDialog,
    ModalClose,
    Textarea,
    Checkbox,
    Avatar,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import CheckRoundedIcon       from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon       from '@mui/icons-material/CloseRounded';
import LockRoundedIcon        from '@mui/icons-material/LockRounded';
import LocationOnRoundedIcon  from '@mui/icons-material/LocationOnRounded';
import AccessTimeRoundedIcon  from '@mui/icons-material/AccessTimeRounded';
import EditRoundedIcon        from '@mui/icons-material/EditRounded';
import PersonRoundedIcon      from '@mui/icons-material/PersonRounded';
import SaveRoundedIcon        from '@mui/icons-material/SaveRounded';
import LockOpenRoundedIcon    from '@mui/icons-material/LockOpenRounded';
import SwapHorizRoundedIcon   from '@mui/icons-material/SwapHorizRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import ReceiptLongRoundedIcon          from '@mui/icons-material/ReceiptLongRounded';
import AddRoundedIcon                  from '@mui/icons-material/AddRounded';
import CreditCardRoundedIcon           from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon             from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon          from '@mui/icons-material/PointOfSaleRounded';
import SendRoundedIcon                 from '@mui/icons-material/SendRounded';
import DeleteOutlineRoundedIcon        from '@mui/icons-material/DeleteOutlineRounded';
import PaymentNumberWidget             from '../../components/PaymentNumberWidget';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import BirthdayWidget from '../../components/BirthdayWidget';
import { businessTodayISO } from '../../businessDay';
import {
    APIGetMyAvailability, APISetMyAvailability, APIGetMyAssignments,
    APICheckFillAccess,
    APIGetMyFillRequests,
    APICreateFillRequest,
    APIGetAvailableSubstitutions,
    APICreateSubstitutionRequest,
    APIAcceptSubstitution,
    APIDeclineSubstitution,
    APIGetMyStats,
    APIGetMyRankToday,
    APIGetOrders,
    APICreateSaleNotification,
    APICreateOrderSelf,
    APIGetProducts,
    APIGetDiscounts,
    APIGetMySaleNotifications,
    APIGetColleaguesAssignments,
    APIGetMyActiveClaim,
    parseApiError,
} from '../../api';
import GREETINGS from '../../greetings';
import { APICancelSubstitutionRequest, APIGetMySubstitutionRequests } from '../../api';
import MemeLoader from '../../components/MemeLoader';
import QrCodeButton from '../../components/QrCodeButton';
import MissedTransferModal from './MissedTransferModal';


// ── Весёлые тексты для замен ──────────────────────────────────────
const DECLINE_PHRASES = [
    'В смысле не могу? Моги',
    'Не-не-не, можешь',
    'Не позорься, помоги человеку',
    'Опять отмазки? Давай выручай',
    'Не будь букой, нажми заменить',
    'А если бы тебе нужна была замена?',
    'Нельзя просто так взять и не помочь',
    'Error 404: Совесть не найдена, помоги товарищу',
    'Просто сделай это',
    'Loading… Ожидаем пока нажмешь заменить',
    'Твое мое не понимать',
    'Ты же не строитель, не строй из себя непонятно что',
];

const ACCEPT_PHRASES = [
    'Красавчик😎',
    'Я в тебе не сомневался🤝',
    'Так держать🙌',
];

const ALREADY_TAKEN_PHRASES = [
    'А уже все, а надо было раньше',
    'Спасибо за помощь, но замена уже найдена',
    'Если ты не рано, то ты поздно',
    'Поезд уже уехал, замена найдена',
    'Слишком поздно, миссия выполнена',
    'Уже не актуально ❌',
];

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ─── Анимации ─────────────────────────────────────────────────────────────────
const fadeUp = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};


// ─── Утилиты ──────────────────────────────────────────────────────────────────

const DAYS_RU = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS_RU = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Получить понедельник текущей недели
const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay(); // 0=вс
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
};

// Получить следующий понедельник
const getNextWeekStart = () => {
    const monday = getWeekStart();
    monday.setDate(monday.getDate() + 7);
    return monday;
};

// Массив из 7 дней начиная с даты
const getWeekDays = (start) =>
    Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return d;
    });

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

// «Деловой» день: до 06:00 относится к предыдущему дню (как на сервере)
const businessToday = () => businessTodayISO();

const getSubWeeks = () => {
    const thisWeek = getWeekStart();
    const nextWeek = getNextWeekStart();
    return [getWeekDays(thisWeek).map(toISO), getWeekDays(nextWeek).map(toISO)];
};

const fmtDate = (d) => `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;
const fmtDay  = (d) => DAYS_RU[d.getDay()];
const fmtDateFull = (d) => `${DAYS_RU[d.getDay()]}, ${d.getDate()} ${MONTHS_RU[d.getMonth()]}`;

// Окно заполнения: Сб 10:00 – Вс 18:00 (по местному времени)
const isInFillingWindow = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Вс, 6=Сб
    const hours = now.getHours();

    if (day === 6) return hours >= 10;          // Суббота с 10:00
    if (day === 0) return hours < 18;           // Воскресенье до 18:00
    return false;                               // Будни — закрыто
};

// Прошло ли окно заполнения (Вс после 18:00 или будни)
const isDeadlinePassed = () => !isInFillingWindow();

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial:  { opacity: 0, y: 16 },
    animate:  { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:     { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const cardVariants = {
    hidden:  { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1, y: 0,
        transition: { delay: i * 0.06, duration: 0.35, ease: [0.22, 1, 0.36, 1] },
    }),
};

// ── Заголовок «Сегодня ты ...» — текст приходит с сервера (rank_text) ──
// Сервер сам выбирает фразу дня (общую для всех), учитывает место в рейтинге
// и отсутствие продаж (тогда — текст последнего места). Здесь только показываем.
function formatRankTitle(myRank) {
    if (myRank && myRank.rank_text) return myRank.rank_text;
    return 'Мои продажи сегодня';
}

// ─── Компонент: строка дня в расписании ──────────────────────────────────────
function DayRow({ date, available, preferredTime, locked, onChange, index }) {
    const isToday   = toISO(date) === toISO(new Date());
    const isPast    = date < new Date(new Date().setHours(0, 0, 0, 0));
    const dayName   = fmtDay(date);
    const dayDate   = fmtDate(date);

    return (
        <motion.div
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <Box sx={{
                display:      'flex',
                alignItems:   'center',
                gap:          { xs: 1.5, sm: 2 },
                px:           { xs: 2, sm: 2.5 },
                py:           1.4,
                borderRadius: 'var(--joy-radius-lg)',
                border:       '1px solid',
                borderColor:  available
                    ? 'rgba(162,38,226,0.28)'
                    : 'var(--joy-palette-divider)',
                bgcolor:      available
                    ? 'rgba(162,38,226,0.06)'
                    : 'var(--joy-palette-background-level1)',
                transition:   'all .2s',
                opacity:      isPast ? 0.45 : 1,
            }}>
                {/* День недели */}
                <Box sx={{ width: { xs: 32, sm: 40 }, flexShrink: 0, textAlign: 'center' }}>
                    <Typography sx={{
                        fontSize:   11,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: isToday
                            ? 'var(--joy-palette-primary-400)'
                            : 'var(--joy-palette-text-secondary)',
                    }}>
                        {dayName}
                    </Typography>
                    <Typography sx={{
                        fontSize:   14,
                        fontWeight: 700,
                        color: isToday
                            ? 'var(--joy-palette-primary-300)'
                            : 'var(--joy-palette-text-primary)',
                    }}>
                        {date.getDate()}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                        {MONTHS_RU[date.getMonth()]}
                    </Typography>
                </Box>

                {/* Тогл доступности */}
                <Switch
                    checked={available}
                    size='lg'
                    onChange={() => !locked && !isPast && onChange({ available: !available, preferredTime })}
                />

                {/* Метка статуса */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {available ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--joy-palette-text-primary)' }}>
                                Доступен
                            </Typography>
                            {!locked && !isPast && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                    <Box
                                        component="input"
                                        type="time"
                                        value={preferredTime || ''}
                                        onChange={(e) => onChange({ available: true, preferredTime: e.target.value || null })}
                                        sx={{
                                            bgcolor: 'primary.800',
                                            color: '#fff',
                                            border: '1px solid',
                                            borderColor: 'rgba(162,38,226,0.4)',
                                            borderRadius: 'var(--joy-radius-sm)',
                                            fontSize: 12,
                                            fontWeight: 600,
                                            px: 1, py: 0.4,
                                            fontFamily: 'inherit',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            '&:focus': {
                                                borderColor: 'var(--joy-palette-primary-400)',
                                            },
                                        }}
                                    />
                                    {preferredTime && (
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="danger"
                                            onClick={() => onChange({ available: true, preferredTime: null })}
                                            sx={{
                                                minWidth: 24, minHeight: 24,
                                                color: 'var(--joy-palette-text-tertiary)',
                                                bgcolor: 'rgba(248,113,113,0.1)',
                                                '&:hover': { color: '#f87171', bgcolor: 'rgba(248,113,113,0.1)' },
                                            }}
                                        >
                                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                            {(locked || isPast) && preferredTime && (
                                <Chip size="sm" variant="soft" sx={{
                                    bgcolor: 'rgba(162,38,226,0.12)',
                                    color:   'var(--joy-palette-primary-300)',
                                    fontSize: 11,
                                }}>
                                    <AccessTimeRoundedIcon sx={{ fontSize: 12, mr: 0.4 }} />
                                    с {preferredTime}
                                </Chip>
                            )}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                            Недоступен
                        </Typography>
                    )}
                </Box>

                {/* Замок если заблокировано */}
                {locked && (
                    <LockRoundedIcon sx={{ fontSize: 15, color: 'var(--joy-palette-text-tertiary)', flexShrink: 0 }} />
                )}
            </Box>
        </motion.div>
    );
}

// ─── Компонент: карточка назначения ──────────────────────────────────────────
function AssignmentCard({ assignment, index }) {
    const date  = new Date(assignment.date);
    const isToday = toISO(date) === toISO(new Date());

    return (
        <motion.div
            custom={index}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
        >
            <Box sx={{
                display:      'flex',
                alignItems:   'flex-start',
                gap:          1.5,
                px:           2,
                py:           1.5,
                borderRadius: 'var(--joy-radius-lg)',
                border:       '1px solid',
                borderColor:  isToday ? 'rgba(162,38,226,0.4)' : 'var(--joy-palette-divider)',
                bgcolor:      isToday ? 'rgba(162,38,226,0.07)' : 'var(--joy-palette-background-level1)',
                position:     'relative',
                overflow:     'hidden',
            }}>
                {/* Акцентная полоска слева */}
                {isToday && (
                    <Box sx={{
                        position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: 3, bgcolor: 'var(--joy-palette-primary-500)',
                        borderRadius: '3px 0 0 3px',
                    }} />
                )}

                {/* Иконка локации */}
                <Box sx={{
                    mt: '2px',
                    width:        32,
                    height:       32,
                    borderRadius: 'var(--joy-radius-sm)',
                    bgcolor:      'rgba(162,38,226,0.12)',
                    display:      'flex',
                    alignItems:   'center',
                    justifyContent: 'center',
                    flexShrink:   0,
                }}>
                    <LocationOnRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)' }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                            {assignment.location_name}
                        </Typography>
                        {isToday && (
                            <Chip size="sm" sx={{
                                bgcolor:  'var(--joy-palette-primary-500)',
                                color:    '#fff',
                                fontSize: 10,
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                            }}>
                                СЕГОДНЯ
                            </Chip>
                        )}
                        {assignment.is_admin && (
                            <Chip size="sm" variant="outlined" sx={{
                                borderColor: '#f59e0b',
                                color: '#f59e0b',
                                fontSize:    10,
                            }}>
                                <PersonRoundedIcon sx={{ fontSize: 11, mr: 0.3 }} />
                                Админ
                            </Chip>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.4, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontSize: 12, color: 'var(--joy-palette-text-secondary)',
                            display: 'flex', alignItems: 'center', gap: 0.4,
                        }}>
                            <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
                            {assignment.start_time?.slice(0, 5)}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                            {fmtDay(date)}, {fmtDate(date)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </motion.div>
    );
}


// ─── Компонент: миниатюра недели (как у руководителя) ────────────────────────
function WeekMiniature({ weekDays, schedule }) {
    const DAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    const today = toISO(new Date());
    const filledCount = weekDays.filter(d => schedule[toISO(d)]?.available).length;

    return (
        <Box>
            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-primary-400)', mb: 1 }}>
                Заполнено {filledCount} из 7
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.8 }}>
                {weekDays.map(date => {
                    const iso = toISO(date);
                    const isToday = iso === today;
                    const info = schedule[iso];
                    const isActive = info?.available;
                    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));

                    return (
                        <Box key={iso} sx={{ textAlign: 'center', opacity: isPast ? 0.4 : 1 }}>
                            <Typography sx={{
                                fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: isToday ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)',
                            }}>
                                {DAYS_SHORT[date.getDay()]}
                            </Typography>
                            <Box sx={{
                                mt: 0.5, mx: 'auto',
                                width: 28, height: 28, borderRadius: 'var(--joy-radius-sm)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: '1.5px solid',
                                borderColor: isToday
                                    ? 'var(--joy-palette-primary-500)'
                                    : isActive
                                        ? 'rgba(34,197,94,0.5)'
                                        : 'var(--joy-palette-divider)',
                                bgcolor: isActive
                                    ? 'rgba(34,197,94,0.1)'
                                    : 'transparent',
                            }}>
                                <Typography sx={{
                                    fontSize: 12, fontWeight: 700,
                                    color: isToday
                                        ? 'var(--joy-palette-primary-300)'
                                        : isActive
                                            ? '#22c55e'
                                            : 'var(--joy-palette-text-tertiary)',
                                }}>
                                    {date.getDate()}
                                </Typography>
                            </Box>
                            {isActive && info?.preferredTime && (
                                <Typography sx={{ fontSize: 8, color: 'var(--joy-palette-primary-400)', mt: 0.3, fontWeight: 600 }}>
                                    {info.preferredTime}
                                </Typography>
                            )}
                            {isActive && !info?.preferredTime && (
                                <Typography sx={{ fontSize: 8, color: '#22c55e', mt: 0.3, fontWeight: 600 }}>
                                    ✓
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}


// ════════════════════════════════════════════════════════════════════
//  PhotographerDashboard
// ════════════════════════════════════════════════════════════════════
export default function PhotographerDashboard() {
    const { token, account, showToast } = useContext(AppContext);
    const navigate = useNavigate();

    // Суббота или позже — показываем следующую неделю для заполнения
    // До субботы — показываем текущую неделю
    const isSaturdayOrLater = (() => {
        const now = new Date();
        const day = now.getDay(); // 0=Вс, 6=Сб
        return day === 6 || day === 0;
    })();

    const editWeekStart = isSaturdayOrLater ? getNextWeekStart() : getWeekStart();
    const editWeekDays  = getWeekDays(editWeekStart);
    const editWeekEnd   = editWeekDays[6];

    const regularWindowOpen = isInFillingWindow();

    // Стейты для заявки на заполнение
    const [fillAccess,      setFillAccess]      = useState(null);    // { can_fill, deadline }
    const [fillRequests,    setFillRequests]    = useState([]);
    const [fillModalOpen,   setFillModalOpen]   = useState(false);
    const [fillReason,      setFillReason]      = useState('');
    const [fillReceivedPush, setFillReceivedPush] = useState(false);
    const [fillComment,     setFillComment]     = useState('');
    const [fillSending,     setFillSending]     = useState(false);
    const [fillCountdown,   setFillCountdown]   = useState('');
    const [activeClaim, setActiveClaim] = useState(null);
    // true только после того, как номер увели по таймауту (10 мин вышли).
    const [claimExpired, setClaimExpired] = useState(false);
    const [missedModalOpen, setMissedModalOpen] = useState(false);

    const [mySubRequests, setMySubRequests] = useState([]);
    const [loadMySubReqs, setLoadMySubReqs] = useState(true);

    // Разблокировано если: обычное окно или одобренная заявка
    const hasApprovedAccess = fillAccess?.can_fill === true;

    // ── Замена ────────────────────────────────────────────────────────
    const [subModalOpen,     setSubModalOpen]     = useState(false);
    const [subSelectedDates, setSubSelectedDates] = useState([]);
    const [subReason,        setSubReason]        = useState('');
    const [subSending,       setSubSending]       = useState(false);
    const [subDateError,     setSubDateError]     = useState('');

    const [availableSubs,    setAvailableSubs]    = useState([]);
    const [loadSubs,         setLoadSubs]         = useState(true);

    // Стейт расписания: { 'YYYY-MM-DD': { available, preferredTime } }
    const [schedule,     setSchedule]     = useState({});
    const [assignments,  setAssignments]  = useState([]);
    const [loadSched,    setLoadSched]    = useState(true);
    const [loadAssign,   setLoadAssign]   = useState(true);
    const [saving,         setSaving]         = useState(false);
    const [saved,          setSaved]          = useState(false);
    // Пользователь нажал «Изменить» в свёрнутом графике, пока окно доступа открыто.
    const [forceEdit,      setForceEdit]      = useState(false);
    const [serverSchedule, setServerSchedule] = useState(null);

    // График считается отправленным, если на редактируемой неделе есть залоченные дни.
    // После сохранения окно заполнения превращается в read-only — изменения только через запрос.
    const alreadySubmitted = isSaturdayOrLater
        && serverSchedule
        && Object.values(serverSchedule).some(d => d?.locked);
    const locked = (!regularWindowOpen || alreadySubmitted) && !hasApprovedAccess;

    // ── Сводка продаж ─────────────────────────────────────────────
    const [todayOrders,  setTodayOrders]  = useState([]);
    const [todayTotal,   setTodayTotal]   = useState(0);
    const [todayCount,   setTodayCount]   = useState(0);
    const [myRank,       setMyRank]       = useState(null);
    const [saleLines, setSaleLines] = useState([{ value: '', money_type: 'transfer' }]);
    const [saleComment, setSaleComment] = useState('');
    const [saleSending, setSaleSending] = useState(false);
    const saleClientUidRef = useRef(null);   // стабильный UID продажи для идемпотентности

    // ── Соло-режим (точка без админа) ────────────────────────────
    const [products,  setProducts]  = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [soloSubmitting, setSoloSubmitting] = useState(false);

    // ── Стейт расписания ТЕКУЩЕЙ недели (read-only) ───────────────
    const [currentWeekSchedule, setCurrentWeekSchedule] = useState({});
    const [currentWeekAssignments, setCurrentWeekAssignments] = useState([]);
    const [colleagueAssignments, setColleagueAssignments] = useState([]);
    const [loadColleagues, setLoadColleagues] = useState(true);
    const [loadCurrentWeek, setLoadCurrentWeek] = useState(true);

    const currentWeekStart = getWeekStart();
    const currentWeekDays = getWeekDays(currentWeekStart);
    const currentWeekEnd = currentWeekDays[6];
    
    // ── Загрузка доступных замен ──────────────────────────────────
    const loadAvailableSubs = useCallback(async () => {
        setLoadSubs(true);
        const data = await APIGetAvailableSubstitutions(token);
        if (Array.isArray(data)) setAvailableSubs(data);
        setLoadSubs(false);
    }, [token]);

    const loadMySubRequests = useCallback(async () => {
        setLoadMySubReqs(true);
        const data = await APIGetMySubstitutionRequests(token);
        if (Array.isArray(data)) setMySubRequests(data);
        setLoadMySubReqs(false);
    }, [token]);

    // Взяли номер под перевод → первая строка обязана быть переводом.
    useEffect(() => {
        if (!activeClaim) return;
        setSaleLines(prev => (prev.length
            ? prev.map((l, i) => (i === 0 ? { ...l, money_type: 'transfer' } : l))
            : [{ value: '', money_type: 'transfer' }]));
    }, [activeClaim]);

    // Загружаем активный claim один раз при монтировании;
    // дальше всё обновление идёт через onClaimChange из PaymentNumberWidget.
    // Polling нужен только чтобы поймать expire с сервера (когда таймер дошёл до 0).
    useEffect(() => {
        const load = async () => {
            const c = await APIGetMyActiveClaim(token);
            setActiveClaim(c || null);
        };
        load();
        const id = setInterval(load, 60000);   // раз в минуту — только страховка от expire
        return () => clearInterval(id);
    }, [token]);

    // Поллинг fill_requests каждые 30 сек — чтобы заявка обновлялась
    // даже без PUSH-а (например когда фотограф открыл страницу днём раньше)
    useEffect(() => {
        if (!token) return;
        const interval = setInterval(() => {
            APIGetMyFillRequests(token, { week_start: toISO(editWeekStart) })
                .then(requests => {
                    if (Array.isArray(requests)) setFillRequests(requests);
                });
        }, 30000);
        return () => clearInterval(interval);
    }, [token, editWeekStart]);

    // ── Загрузка доступности ──────────────────────────────────────
    const loadAvailability = useCallback(async () => {
        setLoadSched(true);
        const data = await APIGetMyAvailability(token, toISO(editWeekStart));
        const days = Array.isArray(data) ? data : data?.days;
        if (Array.isArray(days)) {
            const map = {};
            days.forEach(item => {
                map[item.date] = {
                    available:     true,
                    preferredTime: item.preferred_time ? item.preferred_time.slice(0, 5) : null,
                    locked:        item.is_locked,
                };
            });
            setSchedule(map);
            setServerSchedule(map);
            if (days.length > 0) setSaved(true);
        }
        setLoadSched(false);
    }, [token]);

    // ── Реакция на push о найденной замене ────────────────────────
    useEffect(() => {
        const sw = navigator.serviceWorker;
        if (!sw) return;
        const onMessage = (event) => {
            const t = event.data?.type;
            if (t === 'substitution_taken' || t === 'substitution_available') {
                loadAvailableSubs();      // перечитать доступные замены
                loadMySubRequests();
                if (event.data?.is_own_request) {
                    loadAssignments?.();  // обновить свои назначения, если автор
                }
            }
        };
        sw.addEventListener('message', onMessage);
        return () => sw.removeEventListener('message', onMessage);
    }, [loadAvailableSubs]);

    // ── Загрузка данных ТЕКУЩЕЙ недели (когда редактируем следующую) ──
    const loadCurrentWeekData = useCallback(async () => {
        if (!isSaturdayOrLater) return; // нужно только в сб/вс
        setLoadCurrentWeek(true);
        const [availData, assignData] = await Promise.all([
            APIGetMyAvailability(token, toISO(currentWeekStart)),
            APIGetMyAssignments(token, toISO(currentWeekStart)),
        ]);
        // Доступность
        const days = Array.isArray(availData) ? availData : availData?.days;
        if (Array.isArray(days)) {
            const map = {};
            days.forEach(item => {
                map[item.date] = {
                    available: true,
                    preferredTime: item.preferred_time ? item.preferred_time.slice(0, 5) : null,
                    locked: item.is_locked,
                };
            });
            setCurrentWeekSchedule(map);
        }
        // Назначения
        const list = Array.isArray(assignData) ? assignData : (assignData?.assignments || []);
        setCurrentWeekAssignments(list);
        setLoadCurrentWeek(false);
    }, [token, isSaturdayOrLater]);

    const loadColleagueAssignments = useCallback(async () => {
        setLoadColleagues(true);
        // Загружаем назначения на все дни текущей недели (от сегодня до конца недели)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const dates = [];
        const d = new Date(today);
        while (d <= weekEnd) {
            dates.push(toISO(d));
            d.setDate(d.getDate() + 1);
        }

        const results = await Promise.all(
            dates.map(date => APIGetColleaguesAssignments(token, date))
        );

        // Объединяем и добавляем дату
        const all = [];
        results.forEach((dayData, i) => {
            if (Array.isArray(dayData)) {
                dayData.forEach(a => all.push({ ...a, date: dates[i] }));
            }
        });
        setColleagueAssignments(all);
        setLoadColleagues(false);
    }, [token]);

    // Сегодняшнее назначение и флаг соло-режима
    const todayISO = businessTodayISO();
    const todayAssignment = useMemo(
        () => assignments.find(a => a.date === todayISO) || null,
        [assignments, todayISO],
    );
    const isSoloMode = todayAssignment ? !todayAssignment.location_has_administrators : false;

    // Право продавать «за фотографа» есть у photographer и admin_photographer.
    // У «чистого» админа точки (role='admin') его нет — блок продажи ему не показываем.
    const canSell = (account?.permissions || []).includes('payment_numbers.claim');


    // ── Загрузка назначений ───────────────────────────────────────
    const loadAssignments = useCallback(async () => {
        setLoadAssign(true);
        // Загружаем назначения за обе недели (текущую + следующую)
        const [dataThis, dataNext] = await Promise.all([
            APIGetMyAssignments(token, toISO(getWeekStart())),
            APIGetMyAssignments(token, toISO(getNextWeekStart())),
        ]);
        const listThis = Array.isArray(dataThis) ? dataThis : (dataThis?.assignments || []);
        const listNext = Array.isArray(dataNext) ? dataNext : (dataNext?.assignments || []);
        // Дедупликация по id
        const merged = [...listThis, ...listNext];
        const seen = new Set();
        const unique = merged.filter(a => {
            if (seen.has(a.id)) return false;
            seen.add(a.id);
            return true;
        });
        setAssignments(unique);
        setLoadAssign(false);
    }, [token]);

    // ── Загрузка продаж за сегодня ───────────────────────────────
    const loadTodayOrders = useCallback(async () => {
        const iso = businessToday();

        // Список чеков (APIGetOrders) фотографу недоступен по правам, поэтому выручку и
        // число оформленных чеков за сегодня берём с сервера (my-rank: today_total / today_count),
        // плюс — свои ещё не оформленные продажи (pending).
        const [mySales, rank] = await Promise.all([
            APIGetMySaleNotifications(token, iso),
            APIGetMyRankToday(token),
        ]);

        if (rank) setMyRank(rank);

        const salesArr = Array.isArray(mySales) ? mySales : [];

        // Выручку берём только из rank.today_total — там уже учтены ВСЕ чеки за
        // сегодня (и по продажам, и по любым другим Order'ам фотографа). Прибавлять
        // сумму pending-продаж НЕЛЬЗЯ: их чек уже создан и уже сидит в today_total,
        // иначе выручка удваивается.
        const ordersTotal = parseFloat(rank?.today_total || 0);

        // Число чеков = число не отменённых продаж (совпадает со списком «История
        // чеков за сегодня»); один логический чек = одна продажа, даже если оплата
        // разбита на перевод+наличные (это несколько Order'ов, но один чек).
        const checksCount = salesArr.filter(n => n.status !== 'cancelled').length;

        setTodayOrders(salesArr);
        setTodayCount(checksCount);
        setTodayTotal(ordersTotal);
    }, [token]);

    useEffect(() => { loadTodayOrders(); }, [loadTodayOrders]);

    useEffect(() => {
        loadAvailability();
        loadAssignments();
        loadCurrentWeekData();
        loadColleagueAssignments();
    }, []);

    useEffect(() => {
        loadAvailableSubs();
        loadMySubRequests();
    }, []);

    // ── Загрузка статуса заявки на заполнение ──────────────────────
    const loadFillAccess = useCallback(async () => {
        const weekStartISO = toISO(editWeekStart);
        const [access, requests] = await Promise.all([
            APICheckFillAccess(token, weekStartISO),
            APIGetMyFillRequests(token, { week_start: toISO(editWeekStart) }),
        ]);
        setFillAccess(access);
        if (Array.isArray(requests)) setFillRequests(requests);
    }, [token]);

    useEffect(() => {
        if (!regularWindowOpen) {
            loadFillAccess();
        }
    }, [regularWindowOpen]); // eslint-disable-line

    useEffect(() => {
        loadAvailableSubs();
    }, []);
    
    // ── Добавить строку оплаты ──────────────────────────────────
    const addSaleLine = () => {
        setSaleLines(prev => [...prev, { value: '', money_type: 'transfer' }]);
    };
    const removeSaleLine = (idx) => {
        setSaleLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    };
    const updateSaleLine = (idx, field, val) => {
        setSaleLines(prev => prev.map((line, i) => i === idx ? { ...line, [field]: val } : line));
    };

    // ── Отправка уведомления о продаже ──────────────────────────
    const handleSendSaleNotification = async () => {
        if (saleSending) return;                       // защита от двойного тапа
        const items = saleLines
            .filter(l => parseFloat(l.value) > 0)
            .map(l => ({ value: parseFloat(l.value), money_type: l.money_type }));

        if (items.length === 0) {
            showToast('Укажите сумму', 'warning');
            return;
        }

        // Комментарий обязателен
        const trimmedComment = saleComment.trim();
        if (!trimmedComment) {
            showToast('Комментарий обязателен — опишите чек кратко', 'warning');
            return;
        }
        if (trimmedComment.length < 3) {
            showToast('Комментарий слишком короткий', 'warning');
            return;
        }

        setSaleSending(true);
        // Привязываем номер, если у фотографа сейчас активный claim и есть строки с типом "transfer"
        const hasTransfer = items.some(i => i.money_type === 'transfer');

        // Клиентская защита: перевод без номера — сразу останавливаем
        if (hasTransfer && !activeClaim?.id) {
            setSaleSending(false);
            showToast('Для приёма перевода сначала получите номер для оплаты', 'warning');
            return;
        }

        // Обратная защита: номер взят, но нет строки перевода — нельзя.
        // Либо первой строкой идёт перевод, либо номер надо сбросить.
        if (activeClaim?.id && !hasTransfer) {
            setSaleSending(false);
            showToast('Номер взят под перевод. Первой строкой укажите «Перевод» или сбросьте номер.', 'warning');
            return;
        }

        // Идемпотентность: один client_uid на одну продажу, переживает ретраи.
        if (!saleClientUidRef.current) {
            saleClientUidRef.current = (crypto?.randomUUID?.() || (String(Date.now()) + Math.random()));
        }
        const payload = { items, comment: trimmedComment, client_uid: saleClientUidRef.current };
        if (hasTransfer && activeClaim?.id) {
            payload.payment_claim_id = activeClaim.id;
        }
        const res = await APICreateSaleNotification(token, payload);

        // Успех = пришло уведомление с id. Это надёжнее, чем !res?.error
        // (в редких краях бэк может вернуть и id, и какое-то поле-предупреждение).
        const isSuccess = res && res.id && !res.error;

        if (isSuccess) {
            saleClientUidRef.current = null;           // следующая продажа — новый UID
            setSaleLines([{ value: '', money_type: 'transfer' }]);
            setSaleComment('');
            if (hasTransfer && activeClaim?.id) {
                // Сервер уже закрыл клайм — синхронизируем локально
                setActiveClaim(null);
            }

            const total = items.reduce((s, i) => s + i.value, 0);
            showToast(`Продажа ${total.toLocaleString('ru-RU')} ₽ отправлена админу ✓`);
            loadTodayOrders();
        } else {
            showToast(parseApiError(res?.error || 'Не удалось отправить'), 'danger');
        }
        setSaleSending(false);
    };

    // ── Валидация дат замены (проверяем график/доступность) ───────
    const validateSubDates = (start, end) => {
        if (!start) { setSubDateError('Укажи дату'); return false; }
        setSubDateError('');
        return true;
    };

    // ── Отправка заявки на замену ─────────────────────────────────
    const handleCreateSubstitution = async () => {
        if (subSelectedDates.length === 0) {
            setSubDateError('Выбери хотя бы одну дату');
            return;
        }

        setSubSending(true);
        // Отправляем отдельную заявку на каждую выбранную дату
        let successCount = 0;
        let lastError = '';
        for (const date of subSelectedDates.sort()) {
            const res = await APICreateSubstitutionRequest(token, {
                start_date: date,
                end_date:   date,
                reason:     subReason,
            });
            if (!res?.error) {
                successCount++;
            } else {
                const errMsg = typeof res.error === 'object' ? res.error?.error : res.error;
                lastError = errMsg || 'Ошибка';
            }
        }
        if (successCount > 0) {
            showToast(successCount > 1
                ? `Отправлено заявок: ${successCount} ✓`
                : 'Заявка на замену отправлена ✓'
            );
            setSubModalOpen(false);
            setSubSelectedDates([]);
            setSubReason('');
            await loadMySubRequests();
        }
        if (lastError) {
            showToast(lastError, 'danger');
        }
        setSubSending(false);
    };

    // ── Согласиться на замену ─────────────────────────────────────
    const handleAcceptSub = async (subId) => {
        const res = await APIAcceptSubstitution(token, subId);
        if (!res?.error) {
            showToast(pickRandom(ACCEPT_PHRASES), 'success');
            // Убираем только эту карточку, остальные остаются
            setAvailableSubs(prev => prev.filter(s => s.id !== subId));
            await loadAssignments();
            await loadAvailability();
        } else {
            const errMsg = typeof res.error === 'object'
                ? (res.error?.error || res.error?.detail)
                : res.error;
            // Замену реально забрали — убираем карточку
            if (errMsg && /поздно|нашли|замену уже/i.test(errMsg)) {
                showToast(pickRandom(ALREADY_TAKEN_PHRASES), 'warning');
                setAvailableSubs(prev => prev.filter(s => s.id !== subId));
            } else {
                // Другие ошибки (свой график, свои назначения и т.д.) — карточку НЕ убираем
                showToast(errMsg || 'Ошибка', 'danger');
            }
        }
    };

    // ── Скрыть заявку у себя (но не для всех) ─────────────────────
    const handleDeclineSub = (subId) => {
        // Не скрываем карточку, не вызываем API — просто показываем прикол
        showToast(pickRandom(DECLINE_PHRASES), 'danger');
    };

    // ── Таймер обратного отсчёта при одобренном окне ───────────────
    useEffect(() => {
        if (!hasApprovedAccess || !fillAccess?.deadline) return;

        const tick = () => {
            const now = new Date();
            const deadline = new Date(fillAccess.deadline);
            const diff = deadline - now;
            if (diff <= 0) {
                setFillCountdown('00:00');
                setFillAccess(prev => ({ ...prev, can_fill: false }));
                return;
            }
            const mins = Math.floor(diff / 60000);
            const secs = Math.floor((diff % 60000) / 1000);
            setFillCountdown(`${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [hasApprovedAccess, fillAccess?.deadline]);

    // ── Отправить заявку на заполнение ─────────────────────────────
    const handleSendFillRequest = async () => {
        setFillSending(true);
        const res = await APICreateFillRequest(token, {
            week_start:     toISO(editWeekStart),
            reason:         fillReason,
            received_push:  fillReceivedPush,
            extra_comment:  fillComment,
        });
        if (!res?.error) {
            showToast('Заявка отправлена ✓');
            setFillModalOpen(false);
            setFillReason('');
            setFillReceivedPush(false);
            setFillComment('');
            await loadFillAccess();
        } else {
            showToast(typeof res.error === 'string' ? res.error : 'Ошибка отправки', 'danger');
        }
        setFillSending(false);
    };

    // ── Изменение дня ─────────────────────────────────────────────
    const handleDayChange = (dateStr, val) => {
        setSchedule(prev => ({ ...prev, [dateStr]: { ...val, locked: false } }));
    };

    // ── Сохранение ───────────────────────────────────────────────
    const handleSave = async () => {
        setSaving(true);
        const days = editWeekDays
            .filter(d => {
                const info = schedule[toISO(d)];
                // только доступные и НЕ заблокированные дни
                return info?.available && !info?.locked;
            })
            .map(d => ({
                date:           toISO(d),
                preferred_time: schedule[toISO(d)]?.preferredTime || null,
            }));

        const res = await APISetMyAvailability(token, days);
        if (!res?.error) {
            setSaved(true);
            setForceEdit(false);          // сохранили — сворачиваем обратно в миниатюру
            setServerSchedule({ ...schedule });
            showToast(days.length ? 'График сохранён ✓' : 'График очищен ✓');
            await loadAvailability();
        } else {
            showToast(res.error?.error || 'Ошибка сохранения', 'danger');
            console.error('save error:', res.error);
        }
        setSaving(false);
    };

    // ── Назначения на эту неделю ──────────────────────────────────
    const thisWeekStart = getWeekStart();
    const thisWeekEnd   = new Date(thisWeekStart);
    thisWeekEnd.setDate(thisWeekEnd.getDate() + 6);

    const upcomingAssignments = assignments
        .filter(a => {
            const now = new Date();
            const d = new Date(a.date);
            const todayStart = new Date(now);
            todayStart.setHours(0, 0, 0, 0);

            // Будущие дни — всегда показываем
            if (d > todayStart) return true;

            // Сегодняшние — показываем только до start_time
            if (d.getTime() === todayStart.getTime()) {
                if (a.start_time) {
                    const [hh, mm] = a.start_time.split(':').map(Number);
                    const shiftStart = new Date(now);
                    shiftStart.setHours(hh, mm, 0, 0);
                    return now < shiftStart;
                }
                return true; // нет start_time — показываем весь день
            }

            // Прошедшие дни — не показываем
            return false;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 10);

    const availableCount = editWeekDays.filter(d => schedule[toISO(d)]?.available).length;

    // ── Проверка: есть ли несохранённые изменения ──────────────
    const hasChanges = (() => {
        if (!serverSchedule) return true; // ещё не загрузилось
        const localDays = editWeekDays
            .filter(d => schedule[toISO(d)]?.available)
            .map(d => ({ date: toISO(d), time: schedule[toISO(d)]?.preferredTime || null }));
        const serverDays = editWeekDays
            .filter(d => serverSchedule[toISO(d)]?.available)
            .map(d => ({ date: toISO(d), time: serverSchedule[toISO(d)]?.preferredTime || null }));
        if (localDays.length !== serverDays.length) return true;
        return localDays.some((ld, i) => ld.date !== serverDays[i].date || ld.time !== serverDays[i].time);
    })();

    // Свернуть в миниатюру: либо залочено, либо в открытом окне всё уже заполнено
    // и сохранено (нет несохранённых правок) и пользователь не жал «Изменить».
    // ВАЖНО: объявляем ПОСЛЕ hasChanges и availableCount, иначе TDZ-ошибка
    // «Cannot access 'hasChanges' before initialization».
    const showCompact = locked
        || (hasApprovedAccess && !hasChanges && availableCount > 0 && !forceEdit);

    // ── Случайное приветствие (стабильное на сессию) ──────────
    const [greeting] = useState(() => {
        const tpl = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
        return tpl.replace(/\{name\}/g, account?.first_name || '');
    });

    const hasChangesRef = useRef(hasChanges);
    useEffect(() => { hasChangesRef.current = hasChanges; }, [hasChanges]);

    const { onRefreshTick } = useContext(AppContext);

    useEffect(() => {
        return onRefreshTick(() => {
            if (hasChangesRef.current) return;   // не затираем несохранённый график
            loadAvailability();
            loadAssignments();
            loadCurrentWeekData();
            loadAvailableSubs();
            loadMySubRequests();
        });
    }, [onRefreshTick]);

    const handleCancelMySubRequest = async (reqId) => {
        const res = await APICancelSubstitutionRequest(token, reqId);
        if (!res?.error) {
            showToast('Запрос на замену отменён');
            await loadMySubRequests();
        } else {
            showToast(res.error?.error || 'Ошибка отмены', 'danger');
        }
    };

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 680, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Приветствие ──────────────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.05 }}
                >
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{
                            fontSize:   22,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.1,
                            color:      'var(--joy-palette-text-primary)',
                        }}>
                            {greeting}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                            Твой личный кабинет
                        </Typography>
                    </Box>
                </motion.div>
                        
                <BirthdayWidget />

                {/* ── Блок: Мои активные запросы на замену ──────────── */}
                {mySubRequests.filter(r => {
                    if (r.status !== 'pending' && r.status !== 'approved') return false;
                    // Скрываем заявки, у которых end_date уже прошёл
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const endDate = new Date(r.end_date);
                    return endDate >= today;
                }).length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.38, delay: 0.07 }}
                    >
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid rgba(245,158,11,0.3)',
                            borderRadius: 'var(--joy-radius-xl)',
                            overflow: 'hidden',
                            mb: 2.5,
                        }}>
                            <Box sx={{
                                px: { xs: 2, sm: 2.5 }, py: 1.8,
                                borderBottom: '1px solid var(--joy-palette-divider)',
                                bgcolor: 'rgba(245,158,11,0.04)',
                            }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    🔍 Поиск замены
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    Твои заявки на замену
                                </Typography>
                            </Box>
                            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                <Stack spacing={1}>
                                    {mySubRequests
                                        .filter(r => {
                                            if (r.status !== 'pending' && r.status !== 'approved') return false;
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            const endDate = new Date(r.end_date);
                                            return endDate >= today;
                                        })
                                        .map((req) => {
                                            const dates = req.start_date === req.end_date
                                                ? fmtDateFull(new Date(req.start_date))
                                                : `${fmtDateFull(new Date(req.start_date))} — ${fmtDateFull(new Date(req.end_date))}`;
                                            return (
                                                <Box key={req.id} sx={{
                                                    p: 2, borderRadius: 'var(--joy-radius-lg)',
                                                    border: '1px solid rgba(245,158,11,0.25)',
                                                    bgcolor: 'rgba(245,158,11,0.04)',
                                                }}>
                                                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                        {dates}
                                                    </Typography>
                                                    <Chip size="sm" sx={{
                                                        mt: 0.5, mb: 1,
                                                        bgcolor: req.status === 'approved' ? 'rgba(59,130,246,0.12)' : 'rgba(245,158,11,0.12)',
                                                        color: req.status === 'approved' ? '#3b82f6' : '#f59e0b',
                                                        fontWeight: 600, fontSize: 11,
                                                    }}>
                                                        {req.status === 'approved' ? 'Ищем замену…' : 'Ожидает одобрения'}
                                                    </Chip>
                                                    {req.reason && (
                                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 1 }}>
                                                            Причина: {req.reason}
                                                        </Typography>
                                                    )}
                                                    {(req.status === 'pending' || req.status === 'approved') && (
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            color="danger"
                                                            onClick={() => handleCancelMySubRequest(req.id)}
                                                            sx={{ fontWeight: 700, fontSize: 12 }}
                                                        >
                                                            Отменить замену
                                                        </Button>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                </Stack>
                            </Box>
                        </Box>
                    </motion.div>
                )}

                {/* ── Блок: Доступные замены (для других фотографов) ──────── */}
                {(() => {
                    const visibleSubs = availableSubs;
                    if (loadSubs || visibleSubs.length === 0) return null;
                    return (
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.38, delay: 0.08 }}
                        >
                            <Box sx={{
                                bgcolor:      'var(--joy-palette-background-level1)',
                                border:       '1px solid rgba(59,130,246,0.3)',
                                borderRadius: 'var(--joy-radius-xl)',
                                overflow:     'hidden',
                                mb:           2.5,
                            }}>
                                <Box sx={{
                                    px: { xs: 2, sm: 2.5 }, py: 1.8,
                                    borderBottom: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'rgba(59,130,246,0.04)',
                                }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        🔄 Нужна замена
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                        Коллеги ищут подмену — помоги, если можешь
                                    </Typography>
                                </Box>
                                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                    <Stack spacing={1}>
                                        {visibleSubs.map((sub) => {
                                            const dates = sub.start_date === sub.end_date
                                                ? fmtDateFull(new Date(sub.start_date))
                                                : `${fmtDateFull(new Date(sub.start_date))} — ${fmtDateFull(new Date(sub.end_date))}`;
                                            return (
                                                <Box key={sub.id} sx={{
                                                    p: 2, borderRadius: 'var(--joy-radius-lg)',
                                                    border: '1px solid rgba(59,130,246,0.25)',
                                                    bgcolor: 'rgba(59,130,246,0.04)',
                                                }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                        <Box>
                                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                Замена за {sub.account_name}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: 12, color: '#3b82f6', fontWeight: 600, mt: 0.2 }}>
                                                                {dates}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                    {sub.reason && (
                                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 1 }}>
                                                            Причина: {sub.reason}
                                                        </Typography>
                                                    )}
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Button
                                                            size="sm"
                                                            color="success"
                                                            onClick={() => handleAcceptSub(sub.id)}
                                                            sx={{ fontWeight: 700, fontSize: 12 }}
                                                        >
                                                            Заменить
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outlined"
                                                            color="danger"
                                                            onClick={() => handleDeclineSub(sub.id)}
                                                            sx={{ fontWeight: 600, fontSize: 12 }}
                                                        >
                                                            Не могу
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </Box>
                            </Box>
                        </motion.div>
                    );
                })()}

                {/* ── Блок: Текущая неделя (read-only, показывается в сб/вс) ──── */}
                {isSaturdayOrLater && (
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.38, delay: 0.09 }}
                    >
                        <Box sx={{
                            bgcolor:      'var(--joy-palette-background-level1)',
                            border:       '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            overflow:     'hidden',
                            mb:           2.5,
                        }}>
                            {/* Шапка */}
                            <Box sx={{
                                px: { xs: 2, sm: 2.5 }, py: 1.8,
                                borderBottom: '1px solid var(--joy-palette-divider)',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <Box>
                                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                        Текущая неделя
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                        {fmtDate(currentWeekStart)} — {fmtDate(currentWeekEnd)}
                                    </Typography>
                                </Box>
                                <Chip size="sm" sx={{
                                    bgcolor: 'rgba(162,38,226,0.1)',
                                    color: 'var(--joy-palette-primary-300)',
                                    fontWeight: 600, fontSize: 11,
                                }}>
                                    Только просмотр
                                </Chip>
                            </Box>

                            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                {loadCurrentWeek ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <MemeLoader />
                                    </Box>
                                ) : (
                                    <>
                                        {/* Миниатюра графика текущей недели */}
                                        <WeekMiniature weekDays={currentWeekDays} schedule={currentWeekSchedule} />

                                        {/* Назначения текущей недели */}
                                        {currentWeekAssignments.length > 0 && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography sx={{
                                                    fontSize: 12, fontWeight: 700,
                                                    color: 'var(--joy-palette-text-secondary)',
                                                    mb: 1, letterSpacing: '0.04em',
                                                }}>
                                                    Назначения на эту неделю
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {currentWeekAssignments
                                                        .filter(a => {
                                                            const d = new Date(a.date);
                                                            const todayStart = new Date();
                                                            todayStart.setHours(0, 0, 0, 0);
                                                            return d >= todayStart;
                                                        })
                                                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                                                        .map((a, i) => (
                                                            <AssignmentCard key={a.id} assignment={a} index={i} />
                                                        ))
                                                    }
                                                </Stack>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </motion.div>
                )}

                {/* ── Блок: График на неделю ────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.1 }}
                >
                    <Box sx={{
                        bgcolor:      'var(--joy-palette-background-level1)',
                        border:       '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow:     'hidden',
                        mb:           2.5,
                    }}>
                        {/* Шапка блока */}
                        <Box sx={{
                            px:           { xs: 2, sm: 2.5 },
                            py:           1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                            display:      'flex',
                            alignItems:   'center',
                            justifyContent: 'space-between',
                        }}>
                            <Box>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    {isSaturdayOrLater ? 'График на следующую неделю' : 'Мой график'}
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    {fmtDate(editWeekStart)} — {fmtDate(editWeekEnd)}
                                    {' · '}
                                    <Box component="span" sx={{ color: 'var(--joy-palette-primary-400)' }}>
                                        {availableCount} из 7
                                    </Box>
                                </Typography>
                            </Box>
                            {locked && (
                                <Chip
                                    size="sm"
                                    startDecorator={<LockRoundedIcon sx={{ fontSize: 12 }} />}
                                    sx={{
                                        bgcolor:    'rgba(162,38,226,0.1)',
                                        color:      'var(--joy-palette-primary-300)',
                                        fontWeight: 600,
                                        fontSize:   11,
                                    }}
                                >
                                    Заблокировано
                                </Chip>
                            )}
                            {hasApprovedAccess && (
                                <Chip
                                    size="sm"
                                    startDecorator={<LockOpenRoundedIcon sx={{ fontSize: 12 }} />}
                                    sx={{
                                        bgcolor:    'rgba(34,197,94,0.12)',
                                        color:      '#22c55e',
                                        fontWeight: 600,
                                        fontSize:   11,
                                    }}
                                >
                                    Доступ открыт · {fillCountdown}
                                </Chip>
                            )}
                        </Box>

                        {/* Список дней или миниатюра */}
                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {loadSched ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <MemeLoader />
                                </Box>
                            ) : showCompact ? (
                                /* Заполнено/заблокировано — показываем миниатюру */
                                <WeekMiniature weekDays={editWeekDays} schedule={schedule} />
                            ) : (
                                /* Можно редактировать — полный список с тумблерами */
                                <Stack spacing={1}>
                                    {editWeekDays.map((date, i) => {
                                        const key  = toISO(date);
                                        const info = schedule[key] || { available: false, preferredTime: null };
                                        return (
                                            <DayRow
                                                key={key}
                                                index={i}
                                                date={date}
                                                available={info.available}
                                                preferredTime={info.preferredTime}
                                                locked={info.locked}
                                                onChange={(val) => handleDayChange(key, val)}
                                            />
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>

                        {/* Футер с кнопками */}
                        {!loadSched && (
                            <Box sx={{
                                px:         { xs: 2, sm: 2.5 },
                                py:         1.8,
                                borderTop:  '1px solid var(--joy-palette-divider)',
                                display:    'flex',
                                gap:        1.5,
                                alignItems: 'center',
                                flexWrap:   'wrap',
                            }}>
                                {!locked && hasChanges && (
                                    <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 600, width: '100%', mb: 0.5 }}>
                                        После сохранения график блокируется — заполни всю неделю. Изменения потом только через запрос.
                                    </Typography>
                                )}

                                {showCompact && hasApprovedAccess && (
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        startDecorator={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setForceEdit(true)}
                                        sx={{
                                            borderColor: 'var(--joy-palette-primary-500)',
                                            color:       'var(--joy-palette-primary-300)',
                                            fontWeight:  600, fontSize: 12,
                                            '&:hover': { bgcolor: 'rgba(162,38,226,0.08)' },
                                        }}
                                    >
                                        Изменить
                                    </Button>
                                )}

                                {!showCompact && (
                                    <Button
                                        onClick={handleSave}
                                        loading={saving}
                                        disabled={!hasChanges}
                                        startDecorator={!hasChanges ? <CheckRoundedIcon /> : <SaveRoundedIcon />}
                                        sx={{
                                            background: (locked || !hasChanges)
                                                ? undefined
                                                : 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                            color:      '#fff',
                                            fontWeight: 700,
                                            fontSize:   13,
                                            border:     'none',
                                            boxShadow:  (locked || !hasChanges) ? 'none' : '0 4px 16px rgba(162,38,226,0.35)',
                                            '&:hover':  !hasChanges ? {} : { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                        }}
                                    >
                                        {!hasChanges ? 'Сохранено' : 'Сохранить график'}
                                    </Button>
                                )}

                                {locked && (
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button
                                            variant="outlined"
                                            size="sm"
                                            startDecorator={<EditRoundedIcon sx={{ fontSize: 14 }} />}
                                            onClick={() => navigate('/change-request')}
                                            sx={{
                                                borderColor: 'var(--joy-palette-primary-500)',
                                                color:       'var(--joy-palette-primary-300)',
                                                fontWeight:  600,
                                                fontSize:    12,
                                                '&:hover': { bgcolor: 'rgba(162,38,226,0.08)' },
                                            }}
                                        >
                                            Запросить изменение
                                        </Button>

                                        {/* Кнопка запроса доступа к заполнению */}
                                        {(() => {
                                            const targetWeek = toISO(editWeekStart);
                                            // Берём САМУЮ СВЕЖУЮ заявку этой недели
                                            const latest = fillRequests
                                                .filter(r => r.week_start === targetWeek)
                                                .sort((a, b) => new Date(b.date_create) - new Date(a.date_create))[0];

                                            if (latest?.status === 'pending') {
                                                return (
                                                    <Chip size="sm" sx={{
                                                        bgcolor:  'rgba(245,158,11,0.12)',
                                                        color:    '#f59e0b',
                                                        px: 2,
                                                        fontWeight: 600, fontSize: 11,
                                                    }}>
                                                        Заявка на рассмотрении
                                                    </Chip>
                                                );
                                            }
                                            if (latest?.status === 'approved' && latest.is_fill_window_open) {
                                                // показ обработан в hasApprovedAccess ниже
                                                return null;
                                            }
                                            if (latest?.status === 'rejected') {
                                                return (
                                                    <Chip size="sm" sx={{
                                                        bgcolor:  'rgba(248,113,113,0.12)',
                                                        color:    '#f87171',
                                                        px: 2,
                                                        fontWeight: 600, fontSize: 11,
                                                    }}>
                                                        Заявка отклонена
                                                    </Chip>
                                                );
                                            }
                                            return (
                                                <Button
                                                    variant="outlined"
                                                    size="sm"
                                                    color="warning"
                                                    startDecorator={<LockOpenRoundedIcon sx={{ fontSize: 14 }} />}
                                                    onClick={() => setFillModalOpen(true)}
                                                    sx={{
                                                        borderColor: 'rgba(245,158,11,0.5)',
                                                        color:       '#f59e0b',
                                                        fontWeight:  600,
                                                        fontSize:    12,
                                                        '&:hover': { bgcolor: 'rgba(245,158,11,0.08)' },
                                                    }}
                                                >
                                                    Запросить доступ заполнить
                                                </Button>
                                            );
                                        })()}
                                    </Box>
                                )}

                                {/* Отсчёт при одобренном окне */}
                                {hasApprovedAccess && (
                                    <Chip size="sm" sx={{
                                        bgcolor:   'rgba(34,197,94,0.12)',
                                        color:     '#22c55e',
                                        fontWeight: 700,
                                        fontSize:   12,
                                    }}>
                                        ⏱ Осталось: {fillCountdown}
                                    </Chip>
                                )}
                            </Box>
                        )}
                    </Box>
                </motion.div>
                
                {/* Блок личной продажи — только для тех, кто продаёт как фотограф
                   (photographer / admin_photographer). «Чистый» админ его не видит. */}
                {canSell && (<>
                    {/* ── Виджет: номер для перевода ───────────────── */}
                    <motion.div variants={fadeUp} style={{ marginBottom: 20 }}>
                        <PaymentNumberWidget
                            claim={activeClaim}
                            onClaimChange={(c) => {
                                setActiveClaim(c);
                                if (c) setClaimExpired(false);   // взяли новый номер — прячем ссылку
                            }}
                            onExpired={() => setClaimExpired(true)}
                            canClaim={!!todayAssignment}
                        />
                    </motion.div>

                    {!activeClaim && (
                        <Box sx={{ textAlign: 'center', mt: -1, mb: 2 }}>
                            <Typography
                                onClick={() => setMissedModalOpen(true)}
                                sx={{
                                    fontSize: 12, color: 'var(--joy-palette-text-tertiary)',
                                    cursor: 'pointer', textDecoration: 'underline',
                                    '&:hover': { color: 'var(--joy-palette-primary-400)' },
                                }}
                            >
                                Номер сбросился, а перевод уже пришёл?
                            </Typography>
                        </Box>
                    )}

                    {/* ── Мои продажи сегодня ───────────────────── */}
                    <motion.div variants={fadeUp}>
                        <Box sx={{
                            bgcolor: 'var(--joy-palette-background-level1)',
                            border: '1px solid var(--joy-palette-divider)',
                            borderRadius: 'var(--joy-radius-xl)',
                            p: { xs: 2, sm: 2.5 },
                            mb: 3,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    {formatRankTitle(myRank)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                    {parseFloat(myRank?.today_reward || 0) > 0 && (
                                        <Chip size="sm" sx={{ bgcolor: 'rgba(34,197,94,0.12)', color: '#22c55e', fontSize: 10, fontWeight: 700 }}>
                                            +{parseFloat(myRank.today_reward).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽ премия
                                        </Chip>
                                    )}
                                    <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.08)', color: 'var(--joy-palette-primary-400)', fontSize: 10, fontWeight: 700 }}>
                                        {todayCount} чек.
                                    </Chip>
                                </Box>
                            </Box>

                            {/* Итого */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1.5,
                                px: 2, py: 1.2, mb: 1.5,
                                borderRadius: 'var(--joy-radius-lg)',
                                bgcolor: 'rgba(162,38,226,0.08)',
                                border: '1px solid var(--joy-palette-divider)',
                            }}>
                                <AccountBalanceWalletRoundedIcon sx={{ fontSize: 20, color: 'var(--joy-palette-primary-400)' }} />
                                <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'var(--joy-palette-primary-300)', lineHeight: 1 }}>
                                    {todayTotal.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                </Typography>
                            </Box>

                            {/* Строки оплаты */}
                            <Stack sx={{ gap: 0.8 }}>
                                {saleLines.map((line, idx) => (
                                    <Stack direction={'row' } key={idx} sx={{ flexWrap: 'wrap', gap: 0.8, alignItems: 'center' }}>
                                        <Input
                                            type="number"
                                            placeholder="Сумма"
                                            size="sm"
                                            value={line.value}
                                            onChange={(e) => updateSaleLine(idx, 'value', e.target.value)}
                                            endDecorator={
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>
                                            }
                                        />
                                        <Stack direction={'row'} sx={{ gap: 0.8, alignItems: 'center' }}>
                                            {[
                                                { v: 'transfer', label: 'Перевод',  icon: <CreditCardRoundedIcon  sx={{ fontSize: 16 }} />, c: '#818cf8' },
                                                { v: 'cash',     label: 'Наличные', icon: <PaymentsRoundedIcon    sx={{ fontSize: 16 }} />, c: '#22c55e' },
                                                { v: 'terminal', label: 'Терминал', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 16 }} />, c: '#f59e0b' },
                                            ].map(t => {
                                                const active = line.money_type === t.v;
                                                // Взят номер → первая строка только «Перевод».
                                                const lockedToTransfer = !!activeClaim && idx === 0 && t.v !== 'transfer';
                                                return (
                                                    <IconButton
                                                        key={t.v}
                                                        size="sm"
                                                        variant="plain"
                                                        disabled={lockedToTransfer}
                                                        onClick={() => {
                                                            if (lockedToTransfer) return;
                                                            updateSaleLine(idx, 'money_type', t.v);
                                                        }}
                                                        title={lockedToTransfer ? 'Номер взят под перевод — первая строка только «Перевод»' : t.label}
                                                        sx={{
                                                            minWidth: 36, minHeight: 36,
                                                            borderRadius: 'var(--joy-radius-md)',
                                                            transition: 'all .18s cubic-bezier(0.22, 1, 0.36, 1)',
                                                            ...(active ? {
                                                                color: '#fff', bgcolor: t.c, border: `1px solid ${t.c}`,
                                                                boxShadow: `0 0 0 3px ${t.c}33, 0 4px 14px ${t.c}66`,
                                                                transform: 'scale(1.05)',
                                                                '&:hover': { bgcolor: t.c, transform: 'scale(1.08)' },
                                                            } : {
                                                                color: t.c, bgcolor: 'transparent', border: `1px solid ${t.c}40`,
                                                                '&:hover': { bgcolor: `${t.c}14`, border: `1px solid ${t.c}80` },
                                                            }),
                                                            "& > .MuiSvgIcon-root": { color: active ? 'white' : t.c },
                                                        }}
                                                    >
                                                        {t.icon}
                                                    </IconButton>
                                                );
                                            })}
                                        </Stack>

                                        {saleLines.length > 1 && !(activeClaim && idx === 0) && (
                                            <IconButton size="sm" variant="plain"
                                                onClick={() => removeSaleLine(idx)}
                                                sx={{ color: '#ef4444', fontSize: 14 }}
                                            >
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        )}

                                        {activeClaim && line.money_type === 'transfer' && (
                                            <Box sx={{
                                                p: 1.2,
                                                bgcolor: 'rgba(34,197,94,0.08)',
                                                border: '1px solid rgba(34,197,94,0.25)',
                                                borderRadius: 'var(--joy-radius-md)',
                                                display: 'flex', alignItems: 'center', gap: 1,
                                            }}>
                                                <Typography sx={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>
                                                    ✓ Номер привяжется к чеку
                                                </Typography>
                                                <Typography sx={{ fontSize: 10, flex: 1 }}>
                                                    {activeClaim.payment_number_detail?.bank_display} · {activeClaim.payment_number_detail?.phone}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                ))}
                            </Stack>

                            {/* Добавить строку + Комментарий */}
                            <Box sx={{ mt: 1, display: 'flex', gap: 0.8, alignItems: 'flex-start' }}>
                                <Button
                                    size="sm" variant="plain"
                                    onClick={addSaleLine}
                                    startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                    sx={{
                                        fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-primary-400)',
                                        px: 1, minHeight: 28,
                                    }}
                                >
                                    Добавить строку
                                </Button>
                            </Box>

                            <Box sx={{ mt: 1.2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.06em',
                                        textTransform: 'uppercase',
                                        color: 'var(--joy-palette-text-secondary)',
                                    }}>
                                        Комментарий <Box component="span" sx={{ color: '#f87171' }}>*</Box>
                                    </Typography>
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                        {saleComment.length}/200
                                    </Typography>
                                </Box>
                                <Textarea
                                    placeholder={'Хвались, что продал?\n(Имя, мессенджер\nНомера кадров\nНомер флешки)'}
                                    size="sm"
                                    minRows={2}
                                    maxRows={4}
                                    value={saleComment}
                                    onChange={(e) => {
                                        const v = e.target.value.slice(0, 200);
                                        setSaleComment(v);
                                    }}
                                    sx={{
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid',
                                        borderColor: saleComment.trim()
                                            ? 'var(--joy-palette-divider)'
                                            : 'rgba(248,113,113,0.4)',
                                        fontSize: 13,
                                        color: 'white',
                                        '&:focus-within': {
                                            borderColor: saleComment.trim()
                                                ? 'var(--joy-palette-primary-500)'
                                                : 'rgba(248,113,113,0.9)',
                                            boxShadow: saleComment.trim()
                                                ? 'none'
                                                : '0 0 0 3px rgba(248,113,113,0.12)',
                                        },
                                    }}
                                />
                                {!saleComment.trim() && (
                                    <Typography sx={{ mt: 0.4, fontSize: 10, color: '#f87171', fontWeight: 600 }}>
                                        Без комментария админ не поймёт, что за чек
                                    </Typography>
                                )}
                            </Box>

                            {/* Кнопка отправки */}
                            <Button
                                loading={saleSending}
                                disabled={saleSending}
                                onClick={handleSendSaleNotification}
                                disabled={
                                    !todayAssignment ||
                                    !saleComment.trim() ||
                                    saleLines.every(l => !parseFloat(l.value)) ||
                                    (saleLines.some(l => l.money_type === 'transfer' && parseFloat(l.value) > 0) && !activeClaim) ||
                                    (!!activeClaim && !saleLines.some(l => l.money_type === 'transfer' && parseFloat(l.value) > 0))
                                }
                                startDecorator={<SendRoundedIcon sx={{ fontSize: 17 }} />}
                                sx={{
                                    mt: 1.5,
                                    py: 1.1,
                                    background: 'linear-gradient(135deg, #A226E2 0%, #c56ffa 100%)',
                                    color: '#fff',
                                    fontWeight: 800,
                                    fontSize: 14,
                                    letterSpacing: '0.02em',
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(162, 38, 226, 0.45)',
                                    transition: 'all .2s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #b24aef 0%, #da9eff 100%)',
                                        boxShadow: '0 6px 22px rgba(162, 38, 226, 0.65)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                        boxShadow: '0 2px 10px rgba(162, 38, 226, 0.4)',
                                    },
                                    '&.Mui-disabled, &[disabled]': {
                                        background: 'var(--joy-palette-background-level2)',
                                        color: 'var(--joy-palette-text-tertiary)',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {activeClaim && saleLines.some(l => l.money_type === 'transfer')
                                    ? 'Подтвердить оплату и отправить чек'
                                    : 'Отправить админу'}
                            </Button>

                            {!todayAssignment && (
                                <Typography sx={{
                                    fontSize: 11, color: '#f87171', fontWeight: 700,
                                    textAlign: 'center', mt: 0.6,
                                }}>
                                    Сегодня вы не в смене — продажу оформить нельзя
                                </Typography>
                            )}

                            {saleLines.some(l => l.money_type === 'transfer' && parseFloat(l.value) > 0) && !activeClaim && (
                                <Typography sx={{
                                    fontSize: 10, color: '#f87171', fontWeight: 600,
                                    textAlign: 'center', mt: 0.6,
                                }}>
                                    Для перевода сначала получите номер выше («Получить оплату»)
                                </Typography>
                            )}

                            {activeClaim && saleLines.some(l => l.money_type === 'transfer') && (
                                <Typography sx={{
                                    fontSize: 10, color: 'var(--joy-palette-text-tertiary)',
                                    textAlign: 'center', mt: 0.6, fontStyle: 'italic',
                                }}>
                                    Нажав, вы подтвердите получение оплаты и закроете номер
                                </Typography>
                            )}
                        </Box>
                    </motion.div>
                </>)}

                {/* ── Кнопка: QR-код для клиента ────────────────────────── */}
                <motion.div variants={fadeUp}>
                    <QrCodeButton />
                </motion.div>

                {/* ── Блок: История чеков за сегодня ───────────────────── */}
                <motion.div variants={fadeUp}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow: 'hidden',
                        mb: 3,
                    }}>
                        <Box sx={{
                            px: { xs: 2, sm: 2.5 }, py: 1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                История чеков за сегодня
                            </Typography>
                            <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.08)', color: 'var(--joy-palette-primary-400)', fontSize: 10, fontWeight: 700 }}>
                                {todayOrders.filter(n => n.status !== 'cancelled').length}
                            </Chip>
                        </Box>

                        {todayOrders.filter(n => n.status !== 'cancelled').length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                    Сегодня чеков пока нет
                                </Typography>
                            </Box>
                        ) : (
                            <Stack>
                                {todayOrders
                                    .filter(n => n.status !== 'cancelled')
                                    .map((n, idx, arr) => {
                                        const st = n.status === 'processed'
                                            ? { label: 'Оформлен', color: '#22c55e', bg: 'rgba(34,197,94,0.10)' }
                                            : { label: 'Ожидает',  color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' };
                                        const time = n.date_create
                                            ? new Date(n.date_create).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                            : '';
                                        return (
                                            <Box key={n.id} sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: { xs: 2, sm: 2.5 }, py: 1.2,
                                                borderBottom: idx < arr.length - 1 ? '1px solid var(--joy-palette-divider)' : 'none',
                                            }}>
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', fontVariantNumeric: 'tabular-nums', minWidth: 42 }}>
                                                    {time}
                                                </Typography>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    {n.comment && (
                                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {n.comment}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Chip size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700, bgcolor: st.bg, color: st.color, flexShrink: 0 }}>
                                                    {st.label}
                                                </Chip>
                                                <Typography sx={{ fontSize: 15, fontWeight: 800, minWidth: 62, textAlign: 'right', color: 'var(--joy-palette-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                                                    {parseFloat(n.total_value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                            </Stack>
                        )}
                    </Box>
                </motion.div>

                {/* ── Блок: Мои назначения ─────────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.18 }}
                >
                    <Box sx={{
                        bgcolor:      'var(--joy-palette-background-level1)',
                        border:       '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow:     'hidden',
                    }}>
                        <Box sx={{
                            px:           { xs: 2, sm: 2.5 },
                            py:           1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                            display:      'flex',
                            alignItems:   'center',
                            justifyContent: 'space-between',
                        }}>
                            <Box>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                    Мои назначения
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    Твои предстоящие смены
                                </Typography>
                            </Box>
                            <Button
                                size="sm"
                                variant="outlined"
                                startDecorator={<SwapHorizRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setSubModalOpen(true)}
                                sx={{
                                    borderColor: 'rgba(59,130,246,0.4)',
                                    color:       '#3b82f6',
                                    fontWeight:  600,
                                    fontSize:    11,
                                    '&:hover': { bgcolor: 'rgba(59,130,246,0.08)' },
                                }}
                            >
                                Попросить замену
                            </Button>
                        </Box>

                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {loadAssign ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <MemeLoader />
                                </Box>
                            ) : upcomingAssignments.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                        У тебя пока нет назначений
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1}>
                                    {upcomingAssignments.map((a, i) => (
                                        <AssignmentCard key={a.id} assignment={a} index={i} />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Блок: Общий график (с кем в смене) ─────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.22 }}
                >
                    <Box sx={{
                        bgcolor:      'var(--joy-palette-background-level1)',
                        border:       '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        overflow:     'hidden',
                        mt:           2.5,
                    }}>
                        <Box sx={{
                            px: { xs: 2, sm: 2.5 }, py: 1.8,
                            borderBottom: '1px solid var(--joy-palette-divider)',
                        }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                Общий график
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                С кем ты в смене на этой неделе
                            </Typography>
                        </Box>
                        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                            {loadColleagues ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <MemeLoader />
                                </Box>
                            ) : colleagueAssignments.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Нет назначений на ближайшие дни
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.5}>
                                    {/* Группируем по дате */}
                                    {[...new Set(colleagueAssignments.map(a => a.date))].sort().map(date => {
                                        const dayAssignments = colleagueAssignments.filter(a => a.date === date);
                                        const d = new Date(date);
                                        const isToday = toISO(d) === toISO(new Date());

                                        return (
                                            <Box key={date}>
                                                <Typography sx={{
                                                    fontSize: 12, fontWeight: 700,
                                                    color: isToday ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-secondary)',
                                                    mb: 0.8,
                                                }}>
                                                    {isToday ? '📍 Сегодня' : fmtDateFull(d)}
                                                </Typography>
                                                {/* Группируем по локации */}
                                                {[...new Set(dayAssignments.map(a => a.location_name))].map(loc => {
                                                    const locAssignments = dayAssignments.filter(a => a.location_name === loc);
                                                    return (
                                                        <Box key={loc} sx={{
                                                            p: 1.5, mb: 0.8,
                                                            borderRadius: 'var(--joy-radius-lg)',
                                                            border: '1px solid var(--joy-palette-divider)',
                                                            bgcolor: isToday ? 'rgba(162,38,226,0.04)' : 'transparent',
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
                                                                <LocationOnRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-primary-400)' }} />
                                                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                    {loc}
                                                                </Typography>
                                                            </Box>
                                                            <Stack spacing={0.3}>
                                                                {[...locAssignments]
                                                                    .sort((a, b) => (a.start_time || '99:99').localeCompare(b.start_time || '99:99'))
                                                                    .map(a => {
                                                                    const isMe = a.account_id === account?.id;
                                                                    return (
                                                                        <Box key={a.id} sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            py: 0.4,
                                                                            px: 1,
                                                                            borderRadius: 'var(--joy-radius-sm)',
                                                                            border: isMe
                                                                                ? '1.5px solid rgba(162,38,226,0.5)'
                                                                                : '1.5px solid transparent',
                                                                            bgcolor: isMe
                                                                                ? 'rgba(162,38,226,0.08)'
                                                                                : 'transparent',
                                                                        }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                                                <Avatar src={a.account_avatar_url || undefined} size="sm" sx={{
                                                                                    width: 24, height: 24, fontSize: 10, fontWeight: 700,
                                                                                    bgcolor: 'var(--joy-palette-background-level3)',
                                                                                    color: 'var(--joy-palette-text-tertiary)',
                                                                                    border: '1px solid var(--joy-palette-divider)', flexShrink: 0,
                                                                                }}>
                                                                                    {(a.account_name || '').charAt(0).toUpperCase()}
                                                                                </Avatar>
                                                                                <Typography sx={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: isMe ? 700 : 500,
                                                                                    color: isMe
                                                                                        ? 'var(--joy-palette-primary-300)'
                                                                                        : 'var(--joy-palette-text-primary)',
                                                                                }}>
                                                                                    {a.account_name}
                                                                                </Typography>
                                                                                {a.is_admin && (
                                                                                    <Chip size="sm" sx={{
                                                                                        fontSize: 9, fontWeight: 700,
                                                                                        bgcolor: 'rgba(245,158,11,0.15)',
                                                                                        color: '#f59e0b',
                                                                                        border: '1px solid rgba(245,158,11,0.3)',
                                                                                        height: 18,
                                                                                    }}>
                                                                                        Админ
                                                                                    </Chip>
                                                                                )}
                                                                            </Box>
                                                                            {a.start_time && (
                                                                                <Typography sx={{
                                                                                    fontSize: 12, fontWeight: 600,
                                                                                    color: 'var(--joy-palette-text-secondary)',
                                                                                }}>
                                                                                    с {a.start_time}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Stack>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Модалка: Попросить замену ────────────────────────────── */}
                <Modal open={subModalOpen} onClose={() => setSubModalOpen(false)}>
                    <ModalDialog sx={{
                        // На мобильных — во весь экран
                        width:       { xs: '100%', sm: '90vw' },
                        maxWidth:    { xs: '100%', sm: 400 },
                        height:      { xs: '100%', sm: 'auto' },
                        maxHeight:   { xs: '100%', sm: '85vh' },
                        borderRadius:{ xs: 0, sm: 'var(--joy-radius-xl)' },
                        m:           { xs: 0, sm: 'auto' },
                        overflow:    'auto',
                        bgcolor:     'var(--joy-palette-background-surface)',
                        border:      { xs: 'none', sm: '1px solid var(--joy-palette-divider)' },
                    }}>
                        <ModalClose />
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                            Попросить замену
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            Укажи даты и причину. Заявку рассмотрит руководитель и ответит тебе)
                        </Typography>

                        <Stack spacing={2}>
                            {/* Календарная сетка выбора дат */}
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                    Выбери даты для замены
                                </Typography>
                                {(() => {
                                    const weeks = getSubWeeks();
                                    const DOW = ['ПН','ВТ','СР','ЧТ','ПТ','СБ','ВС'];
                                    const todayISO = toISO(new Date());
                                    const allSchedules = { ...schedule, ...currentWeekSchedule };

                                    const isInRange = (iso) => subSelectedDates.includes(iso);

                                    const handleDayClick = (iso) => {
                                        if (iso < todayISO) return;
                                        // Нельзя выбрать день, в который не работаешь
                                        if (!allSchedules[iso]?.available) return;
                                        setSubSelectedDates(prev =>
                                            prev.includes(iso)
                                                ? prev.filter(d => d !== iso)
                                                : [...prev, iso]
                                        );
                                        setSubDateError('');
                                    };

                                    return (
                                        <Stack spacing={1.6}>
                                            {weeks.map((week, wi) => (
                                                <Box key={wi}>
                                                    <Typography sx={{
                                                        fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
                                                        textTransform: 'uppercase', color: 'var(--joy-palette-text-tertiary)',
                                                        mb: 0.8,
                                                    }}>
                                                        {wi === 0 ? 'Эта неделя' : 'Следующая неделя'}
                                                    </Typography>
                                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.8 }}>
                                                        {week.map((iso, di) => {
                                                            const d = new Date(iso);
                                                            const working = allSchedules[iso]?.available;
                                                            const selected = isInRange(iso);
                                                            const isToday = iso === todayISO;
                                                            const isPast = iso < todayISO;
                                                            const accent = working ? '#22c55e' : 'var(--joy-palette-primary-400)';
                                                            const isNotWorking = !allSchedules[iso]?.available;
                                                            const isDisabledSub = isPast || isNotWorking;

                                                            return (
                                                                <Box
                                                                    key={iso}
                                                                    onClick={(e) => {
                                                                        if (!isDisabledSub) {
                                                                            e.preventDefault();
                                                                            handleDayClick(iso);
                                                                        }
                                                                    }}
                                                                    onTouchEnd={(e) => {
                                                                        // Предотвращаем "призрачный" клик после touch
                                                                        if (!isPast) {
                                                                            e.preventDefault();
                                                                            handleDayClick(iso);
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        textAlign: 'center',
                                                                        cursor: isDisabledSub ? 'not-allowed' : 'pointer',
                                                                        opacity: isDisabledSub ? 0.35 : 1,
                                                                        touchAction: 'manipulation',
                                                                        userSelect: 'none',
                                                                        WebkitTapHighlightColor: 'transparent',
                                                                    }}
                                                                >
                                                                    <Typography sx={{
                                                                        fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                                                                        color: isToday ? 'var(--joy-palette-primary-400)' : 'var(--joy-palette-text-tertiary)',
                                                                    }}>
                                                                        {DOW[di]}
                                                                    </Typography>
                                                                    <Box sx={{
                                                                        mt: 0.5, mx: 'auto',
                                                                        width: 34, height: 34, borderRadius: 'var(--joy-radius-md)',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        border: '2px solid',
                                                                        borderColor: selected
                                                                            ? accent
                                                                            : working
                                                                                ? 'rgba(34,197,94,0.5)'
                                                                                : 'rgba(162,38,226,0.5)',
                                                                        bgcolor: selected
                                                                            ? (working ? 'rgba(34,197,94,0.15)' : 'rgba(162,38,226,0.15)')
                                                                            : (working ? 'rgba(34,197,94,0.04)' : 'rgba(162,38,226,0.06)'),
                                                                        boxShadow: selected ? `0 0 0 3px ${working ? 'rgba(34,197,94,0.18)' : 'rgba(162,38,226,0.18)'}` : 'none',
                                                                        transition: 'all .15s',
                                                                    }}>
                                                                        <Typography sx={{
                                                                            fontSize: 15, fontWeight: 800,
                                                                            color: selected ? accent : (working ? '#22c55e' : 'var(--joy-palette-text-secondary)'),
                                                                        }}>
                                                                            {d.getDate()}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography sx={{ fontSize: 9, color: working ? '#22c55e' : 'var(--joy-palette-primary-400)', mt: 0.3, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                                        {working ? 'работаю' : 'выходной'}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            ))}
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 12, height: 12, borderRadius: 'var(--joy-radius-xs)', bgcolor: 'rgba(34,197,94,0.15)', border: '1.5px solid rgba(34,197,94,0.5)' }} />
                                                    <Typography sx={{ fontSize: 11, color: '#22c55e', fontWeight: 600 }}>работаю</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Box sx={{ width: 12, height: 12, borderRadius: 'var(--joy-radius-xs)', bgcolor: 'rgba(162,38,226,0.08)', border: '1.5px solid rgba(162,38,226,0.5)' }} />
                                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-primary-400)', fontWeight: 600 }}>выходной</Typography>
                                                </Box>
                                            </Box>
                                            {subSelectedDates.length > 0 && (
                                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-primary-300)', fontWeight: 600 }}>
                                                    Выбрано: {subSelectedDates
                                                        .sort()
                                                        .map(d => fmtDateFull(new Date(d)))
                                                        .join(', ')}
                                                </Typography>
                                            )}
                                        </Stack>
                                    );
                                })()}
                            </Box>

                            {subDateError && (
                                <Typography sx={{ fontSize: 12, color: '#f87171', fontWeight: 600 }}>
                                    {subDateError}
                                </Typography>
                            )}

                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                    Причина
                                </Typography>
                                <Textarea
                                    minRows={2}
                                    value={subReason}
                                    onChange={(e) => setSubReason(e.target.value)}
                                    placeholder="Почему нужна замена..."
                                    sx={{ color: 'white', bgcolor: 'var(--joy-palette-background-level1)' }}
                                />
                            </Box>

                            {/* Подсказка: показываем график и назначения на выбранные даты */}
                            {subSelectedDates.length > 0 && (() => {
                                const allSchedules = { ...schedule, ...currentWeekSchedule };
                                const scheduleDays = [];
                                for (const iso of subSelectedDates.sort()) {
                                    if (allSchedules[iso]?.available) {
                                        scheduleDays.push({ date: iso, time: allSchedules[iso]?.preferredTime });
                                    }
                                }
                                // Назначения
                                const matchingAssign = upcomingAssignments.filter(a =>
                                    subSelectedDates.includes(a.date)
                                );
                                if (scheduleDays.length === 0 && matchingAssign.length === 0) return (
                                    <Typography sx={{ fontSize: 12, color: '#f87171' }}>
                                        ⚠ Ты не в графике на выбранные даты
                                    </Typography>
                                );
                                return (
                                    <Box sx={{
                                        p: 1.5, borderRadius: 'var(--joy-radius-md)',
                                        bgcolor: 'rgba(34,197,94,0.06)',
                                        border: '1px solid rgba(34,197,94,0.2)',
                                    }}>
                                        {scheduleDays.length > 0 && (
                                            <>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22c55e', mb: 0.5 }}>
                                                    В графике на выбранные даты:
                                                </Typography>
                                                {scheduleDays.map(d => (
                                                    <Typography key={d.date} sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                                                        • {d.date}{d.time ? ` (с ${d.time})` : ''}
                                                    </Typography>
                                                ))}
                                            </>
                                        )}
                                        {matchingAssign.length > 0 && (
                                            <>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22c55e', mb: 0.5, mt: scheduleDays.length > 0 ? 0.8 : 0 }}>
                                                    Назначения:
                                                </Typography>
                                                {matchingAssign.map(a => (
                                                    <Typography key={a.id} sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                                                        • {a.date} — {a.location_name} ({a.start_time?.slice(0,5)})
                                                    </Typography>
                                                ))}
                                            </>
                                        )}
                                    </Box>
                                );
                            })()}

                            <Button
                                onClick={handleCreateSubstitution}
                                loading={subSending}
                                disabled={subSelectedDates.length === 0}
                                variant="solid"
                                color="primary"
                                sx={{
                                    color: '#fff', fontWeight: 700, fontSize: 13,
                                    border: 'none',
                                    '&:disabled': { opacity: 0.5 },
                                }}
                            >
                                Отправить заявку на замену
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>
                
                {/* ── Модальное окно: Запрос доступа к заполнению ─── */}
                <Modal open={fillModalOpen} onClose={() => setFillModalOpen(false)}>
                    <ModalDialog sx={{
                        maxWidth: '90vw',
                        width: "100%",
                        bgcolor: 'var(--joy-palette-background-surface)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                    }}>
                        <ModalClose />
                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'var(--joy-palette-text-primary)', mb: 0.5 }}>
                            Запросить доступ к заполнению
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            Окно заполнения закрыто. Отправь заявку руководителю — если одобрят, у тебя будет 1 час.
                        </Typography>

                        <Stack spacing={2}>
                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                    Почему не заполнил вовремя? *
                                </Typography>
                                <Textarea
                                    minRows={2}
                                    value={fillReason}
                                    onChange={(e) => setFillReason(e.target.value)}
                                    placeholder="Укажите причину..."
                                    sx={{ color: 'white', bgcolor: 'var(--joy-palette-background-level1)' }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Checkbox
                                    checked={fillReceivedPush}
                                    onChange={(e) => setFillReceivedPush(e.target.checked)}
                                    size="sm"
                                />
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)' }}>
                                    Я получил PUSH-напоминание о заполнении
                                </Typography>
                            </Box>

                            <Box>
                                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.5 }}>
                                    Дополнительный комментарий
                                </Typography>
                                <Textarea
                                    minRows={2}
                                    value={fillComment}
                                    onChange={(e) => setFillComment(e.target.value)}
                                    placeholder="Необязательно..."
                                    sx={{ color: 'white', bgcolor: 'var(--joy-palette-background-level1)' }}
                                />
                            </Box>

                            <Button
                                onClick={handleSendFillRequest}
                                loading={fillSending}
                                disabled={!fillReason.trim()}
                                sx={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13,
                                    border: 'none',
                                    '&:hover': { boxShadow: '0 4px 16px rgba(245,158,11,0.4)' },
                                    '&:disabled': { opacity: 0.5 },
                                }}
                            >
                                Отправить заявку
                            </Button>
                        </Stack>
                    </ModalDialog>
                </Modal>

            </Box>

            <MissedTransferModal
                open={missedModalOpen}
                onClose={() => setMissedModalOpen(false)}
                onLinked={(claim) => {
                    setActiveClaim(claim);
                    setMissedModalOpen(false);
                    setClaimExpired(false);
                    showToast('Номер привязан к чеку ✓', 'success');
                }}
            />
        </motion.div>
    );
}